/**
 * Journeys: every finished order of the period, with what went wrong on it — and any one of
 * them replayed.
 *
 * A report like the others: the period is the area's, the export is the same button, and it
 * is the administrator's. The list is narrowed by the usual filters and by the problems the
 * firm's own settings define — late, the long way round, a long wait, a stop on the way,
 * speeding — so the rides worth a look are found without opening every one.
 *
 * Everything that narrows the list lives in the page's address, and so does the order being
 * replayed and the second it is paused on. A filtered list is a link; so is a moment of a
 * ride, which is what one dispatcher sends another with "look at this".
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useTenantFormats } from '../shell/formats';
import { stateTone } from '../orders/states';
import {
  Button,
  Card,
  EmptyState,
  Icon,
  Input,
  PageHeader,
  Pill,
  Segmented,
  Select,
  StatTile,
  Sub,
  Table,
  Td,
  Th,
  Tr,
  cx,
} from '../ui';
import { reportsApi } from './api';
import { ExportButton } from './ExportButton';
import { useReport, type ReportPageProps } from './frame';
import {
  NO_FILTERS,
  PROBLEMS,
  filterParams,
  filterQuery,
  filtersFrom,
  isFiltered,
  problemTone,
  toggleProblem,
  type JourneyFilters,
  type JourneyRow,
  type SourceFilter,
  type StateFilter,
  sourceWord,
} from './journey';
import { JourneyPlayer } from './JourneyPlayer';
import { PeriodRow } from './PeriodRow';

/** How long the search box waits after the last key before it asks. */
const TYPING_PAUSE_MS = 350;

export function JourneysPage({ frame }: ReportPageProps) {
  const [params, setParams] = useSearchParams();
  const orderId = params.get('order');

  const open = useCallback(
    (id: string) => {
      const next = new URLSearchParams(params);

      next.set('order', id);
      next.delete('t');
      setParams(next);
    },
    [params, setParams],
  );

  const back = useCallback(() => {
    const next = new URLSearchParams(params);

    next.delete('order');
    next.delete('t');
    setParams(next);
  }, [params, setParams]);

  const moment = useCallback(
    (seconds: number) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);

          if (seconds > 0) next.set('t', String(seconds));
          else next.delete('t');

          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const t0 = params.get('t');

  return orderId !== null ? (
    <JourneyPlayer
      orderId={orderId}
      startAt={t0 !== null && Number.isFinite(Number(t0)) ? Number(t0) : null}
      onBack={back}
      onMoment={moment}
      onFail={frame.fail}
    />
  ) : (
    <JourneyList frame={frame} onOpen={open} />
  );
}

function JourneyList({ frame, onOpen }: ReportPageProps & { onOpen: (orderId: string) => void }) {
  const { t } = useTranslation();
  const formats = useTenantFormats();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // The address is where the filters live; this is the page's working copy of them.
  const [filters, setFilters] = useState<JourneyFilters>(() => filtersFrom(params));
  const [typed, setTyped] = useState(filters.text);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((current) => (current.text === typed ? current : { ...current, text: typed }));
      setPage(0);
    }, TYPING_PAUSE_MS);

    return () => clearTimeout(timer);
  }, [typed]);

  // Written back to the address on every change, replacing rather than pushing: the back
  // button should leave the report, not undo a chip.
  useEffect(() => {
    setParams(filterParams(filters), { replace: true });
  }, [filters, setParams]);

  const query = filterQuery(filters, page);

  const { data: list } = useReport(frame, (period) => reportsApi.journeys(period, query), query);

  const narrow = (change: Partial<JourneyFilters>) => {
    setFilters((current) => ({ ...current, ...change }));
    setPage(0);
  };

  const rows = useMemo(() => list?.rows ?? [], [list]);
  const counts = list?.problems ?? {};
  const first = list ? list.page * list.pageSize : 0;

  return (
    <>
      <PageHeader
        title={t('nav.reports.journeys')}
        description={t('reports.about.journeys')}
        actions={
          <ExportButton
            frame={frame}
            path="/api/tenant/reports/journeys"
            query={filterQuery(filters)}
          />
        }
      />

      <PeriodRow frame={frame} />

      {list && (
        <div className="grid grid-cols-5 gap-3">
          <Card>
            <StatTile
              label={t('reports.journeys.tiles.orders')}
              value={formats.count(list.total)}
              sub={t('reports.journeys.tiles.ordersSub')}
            />
          </Card>
          {(['late', 'detour', 'longWait', 'speeding'] as const).map((problem) => (
            <Card key={problem}>
              <StatTile
                label={t(`reports.journeys.problems.${problem}`)}
                value={formats.count(counts[problem] ?? 0)}
                tone={(counts[problem] ?? 0) > 0 ? problemTone(problem) : 'neutral'}
                sub={t(`reports.journeys.tiles.${problem}`, {
                  minutes:
                    problem === 'late'
                      ? list.thresholds.lateMinutes
                      : list.thresholds.longWaitMinutes,
                  percent: list.thresholds.detourPercent,
                  town: list.thresholds.inTownSpeedKmh,
                  road: list.thresholds.outOfTownSpeedKmh,
                })}
              />
            </Card>
          ))}
        </div>
      )}

      <Card>
        <div className="flex items-center gap-3 flex-wrap px-3.5 py-2.5 border-b border-rule">
          <label className="relative flex items-center w-[240px]">
            <span className="absolute left-2.5 text-ink-3 pointer-events-none">
              <Icon name="search" small />
            </span>
            <Input
              type="search"
              className="pl-8"
              value={typed}
              placeholder={t('reports.journeys.search')}
              aria-label={t('reports.journeys.search')}
              onChange={(event) => setTyped(event.target.value)}
            />
          </label>

          <Segmented<StateFilter>
            label={t('reports.journeys.state.label')}
            value={filters.state}
            onChange={(state) => narrow({ state })}
            options={[
              { value: 'all', label: t('reports.journeys.state.all') },
              { value: 'Completed', label: t('states.Completed') },
              { value: 'Cancelled', label: t('states.Cancelled') },
              { value: 'NoShow', label: t('states.NoShow') },
            ]}
          />

          <Segmented<SourceFilter>
            label={t('reports.journeys.sourceLabel')}
            value={filters.source}
            onChange={(source) => narrow({ source })}
            options={[
              { value: 'all', label: t('reports.journeys.source.all') },
              { value: 'phone', label: t('reports.journeys.source.phone') },
              { value: 'app', label: t('reports.journeys.source.app') },
              { value: 'street', label: t('reports.journeys.source.street') },
            ]}
          />

          <div className="w-[190px]">
            <Select
              aria-label={t('reports.journeys.driver')}
              value={filters.driver ?? ''}
              onChange={(event) => narrow({ driver: event.target.value || null })}
            >
              <option value="">{t('reports.journeys.everyDriver')}</option>
              {list?.drivers.map((driver) => (
                <option key={driver.id} value={driver.id}>
                  {driver.name}
                </option>
              ))}
            </Select>
          </div>

          <div className="w-[150px]">
            <Select
              aria-label={t('reports.journeys.vehicle')}
              value={filters.vehicle ?? ''}
              onChange={(event) => narrow({ vehicle: event.target.value || null })}
            >
              <option value="">{t('reports.journeys.everyVehicle')}</option>
              {list?.vehicles.map((vehicle) => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicle.name}
                </option>
              ))}
            </Select>
          </div>

          {filters.customer !== null && (
            <Button
              size="sm"
              onClick={() => narrow({ customer: null })}
              title={t('reports.journeys.oneClientClear')}
            >
              {t('reports.journeys.oneClient')}
              <Icon name="close" small />
            </Button>
          )}

          {isFiltered(filters) && (
            <Button
              size="sm"
              variant="quiet"
              onClick={() => {
                setTyped('');
                setFilters(NO_FILTERS);
                setPage(0);
              }}
            >
              {t('reports.journeys.clear')}
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap px-3.5 py-2.5 border-b border-rule">
          <span className="text-secondary text-ink-3 mr-1">
            {t('reports.journeys.onlyProblems')}
          </span>
          {PROBLEMS.map((problem) => {
            const on = filters.problems.includes(problem);

            return (
              <button
                key={problem}
                type="button"
                aria-pressed={on}
                onClick={() => narrow({ problems: toggleProblem(filters.problems, problem) })}
                className={cx(
                  'inline-flex items-center gap-1.5 h-7 px-2.5 rounded-pill border text-secondary cursor-pointer',
                  on
                    ? 'bg-brand text-on-brand border-brand'
                    : 'bg-surface text-ink-2 border-rule hover:bg-surface-2',
                )}
              >
                {t(`reports.journeys.problems.${problem}`)}
                <span className={cx('tabular-nums', on ? 'text-on-brand' : 'text-ink-3')}>
                  {formats.count(counts[problem] ?? 0)}
                </span>
              </button>
            );
          })}
          <Button size="sm" className="ml-auto" onClick={() => navigate('/settings/replay')}>
            {t('reports.journeys.whatCounts')}
          </Button>
        </div>

        {list !== null && rows.length === 0 ? (
          <EmptyState>
            {isFiltered(filters) ? t('reports.journeys.noneMatch') : t('reports.journeys.none')}
          </EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <thead>
                <tr>
                  <Th>{t('reports.journeys.columns.when')}</Th>
                  <Th>{t('reports.journeys.columns.order')}</Th>
                  <Th>{t('reports.journeys.columns.client')}</Th>
                  <Th>{t('reports.journeys.columns.driver')}</Th>
                  <Th>{t('reports.journeys.columns.state')}</Th>
                  <Th numeric>{t('reports.journeys.columns.arrival')}</Th>
                  <Th numeric>{t('reports.journeys.columns.wait')}</Th>
                  <Th numeric>{t('reports.journeys.columns.distance')}</Th>
                  <Th numeric>{t('reports.journeys.columns.fare')}</Th>
                  <Th>{t('reports.journeys.columns.problems')}</Th>
                  <Th>
                    <span className="sr-only">{t('reports.journeys.replay')}</span>
                  </Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <JourneyLine
                    key={row.orderId}
                    row={row}
                    lateMinutes={list?.thresholds.lateMinutes ?? 5}
                    onOpen={() => onOpen(row.orderId)}
                  />
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {list !== null && list.total > list.pageSize && (
          <div className="flex items-center justify-between gap-3 px-3.5 py-2.5 border-t border-rule">
            <Sub>
              {t('reports.journeys.showing', {
                from: formats.count(first + 1),
                to: formats.count(first + rows.length),
                total: formats.count(list.total),
              })}
            </Sub>
            <div className="flex items-center gap-2">
              <Button size="sm" disabled={page === 0} onClick={() => setPage((at) => at - 1)}>
                {t('reports.journeys.newer')}
              </Button>
              <Button
                size="sm"
                disabled={first + rows.length >= list.total}
                onClick={() => setPage((at) => at + 1)}
              >
                {t('reports.journeys.older')}
              </Button>
            </div>
          </div>
        )}
      </Card>

      <p className="text-secondary text-ink-3 max-w-[80ch] m-0">{t('reports.journeys.footnote')}</p>
    </>
  );
}

/** One order. The whole row opens the replay; so does the button at its end. */
function JourneyLine({
  row,
  lateMinutes,
  onOpen,
}: {
  row: JourneyRow;
  lateMinutes: number;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const formats = useTenantFormats();

  const late = row.lateSeconds;

  return (
    <Tr onClick={onOpen}>
      <Td>
        <span className="tabular-nums">{formats.moment(row.requestedAt)}</span>
        {row.scheduledFor && (
          <Sub block>
            {t('reports.journeys.bookedFor', { time: formats.moment(row.scheduledFor) })}
          </Sub>
        )}
      </Td>

      <Td>
        <span className="font-mono tracking-[0.04em]">{row.reference}</span>
        <Sub block>{t(`reports.journeys.source.${sourceWord(row.source)}`)}</Sub>
      </Td>

      <Td>
        <span className="font-semibold">
          {row.customerName ?? row.customerPhone ?? t('reports.journeys.anonymous')}
        </span>
        <Sub block title={row.pickupAddress}>
          {row.pickupAddress}
          {row.destinationAddress ? ` → ${row.destinationAddress}` : ''}
        </Sub>
      </Td>

      <Td>
        {row.driverName ?? <span className="text-ink-3">—</span>}
        {(row.callSign || row.plate) && (
          <Sub block>{[row.callSign, row.plate].filter(Boolean).join(' · ')}</Sub>
        )}
      </Td>

      <Td>
        <Pill soft tone={stateTone(row.state)}>
          {t(`states.${row.state}`, { defaultValue: row.state })}
        </Pill>
      </Td>

      <Td
        numeric
        className={cx('tabular-nums', late != null && late >= lateMinutes * 60 && 'text-danger')}
      >
        {late == null
          ? '—'
          : late >= 0
            ? `+${formats.duration(late)}`
            : `−${formats.duration(-late)}`}
      </Td>

      <Td numeric className="tabular-nums">
        {formats.duration(row.waitSeconds ?? null)}
      </Td>

      <Td numeric className="tabular-nums">
        {row.distanceMetres != null ? formats.distance(row.distanceMetres) : '—'}
        {row.detourPercent != null && (
          <Sub block className={row.problems.includes('detour') ? 'text-danger' : undefined}>
            {t('reports.journeys.versusBest', {
              percent: `${row.detourPercent > 0 ? '+' : ''}${formats.count(Math.round(row.detourPercent))}`,
            })}
          </Sub>
        )}
      </Td>

      <Td numeric className="tabular-nums">
        {row.fareAmount != null ? formats.money(row.fareAmount) : '—'}
      </Td>

      <Td wrap>
        <span className="flex flex-wrap gap-1">
          {row.problems.map((problem) => (
            <Pill key={problem} soft tone={problemTone(problem)}>
              {t(`reports.journeys.problems.${problem}`)}
            </Pill>
          ))}
        </span>
      </Td>

      <Td>
        <Button
          size="sm"
          variant="outline"
          title={t('reports.journeys.replayHelp')}
          onClick={(event) => {
            event.stopPropagation();
            onOpen();
          }}
        >
          <Icon name={row.hasJourney ? 'play' : 'route'} small />
          {t('reports.journeys.replay')}
        </Button>
      </Td>
    </Tr>
  );
}
